import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import CustomButton from "../../../components/buttons/customButton";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import {
    EMBED_SIGNUP_ROUTES,
    FB_EMBED_SIGNUP_CONFIG,
} from "../../../config/apiRoutes";

const toInt = (value) => parseInt(value, 10) || 0;

const OnBoardingHome = () => {
    const { user } = useSelector((state) => state.authentication);
    const client_id = user?.client_id;

    const [loading, setLoading] = useState(true);
    const [snack, setSnack] = useState({ open: false, message: "", severity: "error" });
    const [state, setState] = useState({
        is_signup: 0,
        payment_method: 0,
        reg_cloud_api: 0,
        is_signup_loading: 0,
        payment_method_loading: 0,
        reg_cloud_api_loading: 0,
    });

    const showSnack = useCallback(
        (message, severity = "error") => setSnack({ open: true, message, severity }),
        []
    );

    // 🔹 Load the current on-boarding status on mount
    useEffect(() => {
        let active = true;

        const fetchOnBoard = async () => {
            try {
                if (client_id) {
                    const { data: envelope } = await axios.post(EMBED_SIGNUP_ROUTES.CHECK, {
                        client_id,
                    });
                    const onboard = envelope?.data ?? {};
                    if (active) {
                        setState((prev) => ({
                            ...prev,
                            is_signup: toInt(onboard.is_signup),
                            payment_method: toInt(onboard.payment_method),
                            reg_cloud_api: toInt(onboard.reg_cloud_api),
                        }));
                    }
                }
            } catch (error) {
                console.error("Error fetching on-boarding data:", error);
                if (active) showSnack("Error fetching on-boarding data");
            } finally {
                if (active) setLoading(false);
            }
        };

        fetchOnBoard();
        return () => {
            active = false;
        };
    }, [client_id, showSnack]);

    // 🔹 Load the Facebook SDK once
    useEffect(() => {
        window.fbAsyncInit = function () {
            window.FB?.init({
                appId: FB_EMBED_SIGNUP_CONFIG.APP_ID,
                cookie: true,
                xfbml: true,
                version: FB_EMBED_SIGNUP_CONFIG.GRAPH_VERSION,
            });
        };

        if (document.getElementById("facebook-jssdk")) return;
        const js = document.createElement("script");
        js.id = "facebook-jssdk";
        js.src = "https://connect.facebook.net/en_US/sdk.js";
        document.body.appendChild(js);
    }, []);

    // 🔹 Step 1 — Launch WhatsApp Embedded Signup
    const launchWhatsAppSignup = () => {
        const FB = window.FB;
        if (!FB) {
            showSnack("Facebook SDK is still loading, please try again.");
            return;
        }

        if (window.fbq) {
            window.fbq("trackCustom", "WhatsAppOnboardingStart", {
                appId: FB_EMBED_SIGNUP_CONFIG.APP_ID,
                feature: "whatsapp_embedded_signup",
            });
        }

        FB.login(
            (response) => {
                if (!response.authResponse) {
                    console.log("User cancelled login or did not fully authorize.");
                    return;
                }
                if (!client_id) {
                    showSnack("Client ID is missing.");
                    return;
                }

                (async () => {
                    setState((prev) => ({ ...prev, is_signup_loading: 1 }));
                    try {
                        await axios.post(EMBED_SIGNUP_ROUTES.CREATE, {
                            client_id,
                            code: response.authResponse.code,
                        });
                        setState((prev) => ({ ...prev, is_signup_loading: 0, is_signup: 1 }));
                        showSnack("Sign up flow completed", "success");
                    } catch (error) {
                        console.error("Error completing sign up:", error);
                        setState((prev) => ({ ...prev, is_signup_loading: 0, is_signup: 0 }));
                        showSnack("Error completing sign up");
                    }
                })();
            },
            {
                config_id: FB_EMBED_SIGNUP_CONFIG.CONFIG_ID,
                response_type: "code",
                override_default_response_type: true,
            }
        );
    };

    // 🔹 Step 2 — Verify payment connected to the WhatsApp number
    const updatePaymentVerification = async () => {
        setState((prev) => ({ ...prev, payment_method_loading: 1 }));
        try {
            const response = await axios.post(EMBED_SIGNUP_ROUTES.PAYMENT_UPDATE, { client_id });
            if (response.status === 200) {
                setState((prev) => ({ ...prev, payment_method: 1 }));
                showSnack("Payment verified", "success");
            }
        } catch (error) {
            console.error("Error verifying payment:", error);
            showSnack("Error verifying payment");
        } finally {
            setState((prev) => ({ ...prev, payment_method_loading: 0 }));
        }
    };

    // 🔹 Step 3 — Register Cloud API
    const updateCloudRegistration = async () => {
        setState((prev) => ({ ...prev, reg_cloud_api_loading: 1 }));
        try {
            const response = await axios.post(EMBED_SIGNUP_ROUTES.REGISTER, { client_id });
            if (response.status === 200) {
                setState((prev) => ({ ...prev, reg_cloud_api: 1 }));
                showSnack("Cloud API registered", "success");
            }
        } catch (error) {
            console.error("Error registering Cloud API:", error);
            showSnack("Error registering Cloud API");
        } finally {
            setState((prev) => ({ ...prev, reg_cloud_api_loading: 0 }));
        }
    };

    // 🔹 Sequential gating: a step is enabled only when the previous step is
    // complete and the current step is not yet done.
    const signupDone = state.is_signup > 0;
    const paymentDone = state.payment_method > 0;
    const regDone = state.reg_cloud_api > 0;

    const steps = [
        {
            id: 1,
            title: "1. Sign Up Flow",
            done: signupDone,
            loading: state.is_signup_loading === 1,
            // Step 1 is the entry point — enabled until signup is complete.
            disabled: signupDone || state.is_signup_loading === 1,
            label: signupDone ? "Completed" : "Start",
            loadingLabel: "Starting...",
            onClick: launchWhatsAppSignup,
        },
        {
            id: 2,
            title: "2. Verify payment connected to whatsapp number ?",
            done: paymentDone,
            loading: state.payment_method_loading === 1,
            // Enabled only when signup is done and payment is not yet done.
            disabled: !signupDone || paymentDone || state.payment_method_loading === 1,
            label: paymentDone ? "Verified" : "Verify",
            loadingLabel: "Verifying...",
            onClick: updatePaymentVerification,
        },
        {
            id: 3,
            title: "3. Register Cloud API",
            done: regDone,
            loading: state.reg_cloud_api_loading === 1,
            // Enabled only when payment is done and registration is not yet done.
            disabled: !paymentDone || regDone || state.reg_cloud_api_loading === 1,
            label: regDone ? "Completed" : "Start",
            loadingLabel: "Registering...",
            onClick: updateCloudRegistration,
        },
    ];

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" align="center" sx={{ fontWeight: 700, color: "text.secondary", mb: 2 }}>
                On Boarding
            </Typography>

            <Grid container spacing={2}>
                {steps.map((step) => (
                    <Grid key={step.id} item size={{ xs: 12 }}>
                        <Card>
                            <CardContent>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: 1,
                                    }}
                                >
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {step.title}
                                    </Typography>
                                    <Box sx={{ minWidth: 160 }}>
                                        <CustomButton
                                            status={step.loading ? "loading" : "idle"}
                                            onClick={step.disabled ? undefined : step.onClick}
                                            disabled={step.disabled}
                                            label={step.label}
                                            loadingLabel={step.loadingLabel}
                                            icon={<></>}
                                            color={step.done ? "var(--success)" : undefined}
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <SnackbarMessage
                open={snack.open}
                message={snack.message}
                severity={snack.severity}
                onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
            />
        </Box>
    );
};

export default OnBoardingHome;
